// src/utils/embed-auth-bridge.js
// Handles auth via postMessage when front-chat runs inside an iframe (embed mode).
// The parent (userAdmin-fe) owns the MSAL session and passes tokens down.

import { logger } from "./logger";
import { setCurrentTenant } from "./roles";

const AUTH_READY = "AUTH_READY";
const AUTH_CONTEXT = "AUTH_CONTEXT";
const REFRESH_TOKEN = "REFRESH_TOKEN";

let cachedToken = null;
let parentOrigin = null;
let cleanupListener = null;
let allowedOriginsCache = null;

// Shared promise for the initial token wait (all callers get the same one)
let pendingTokenPromise = null;
let pendingTokenResolve = null;
let pendingTokenTimeout = null;

// Shared promise for token refresh (all concurrent 401 callers get the same one)
let pendingRefreshPromise = null;
let pendingRefreshResolve = null;
let pendingRefreshTimeout = null;

/**
 * Parse and cache allowed origins from env variable (comma-separated).
 * @returns {string[]}
 */
export function getAllowedOrigins() {
  if (allowedOriginsCache) return allowedOriginsCache;

  const raw = process.env.REACT_APP_ALLOWED_PARENT_ORIGIN || "";
  allowedOriginsCache = raw
    .split(",")
    .map((o) => o.trim())
    .filter(Boolean);
  return allowedOriginsCache;
}

/**
 * Validate that the event origin is in the allowlist.
 */
function isOriginAllowed(origin, allowedOrigins) {
  return allowedOrigins.some((allowed) => origin === allowed);
}

/**
 * Validate that AUTH_CONTEXT payload has usable auth data.
 * Accepts either a full token (production) or just an apiKey (local dev mode).
 */
function isValidAuthPayload(data) {
  const hasToken =
    typeof data.accessToken === "string" &&
    data.accessToken.length > 0 &&
    typeof data.idToken === "string" &&
    data.idToken.length > 0 &&
    data.idTokenClaims != null &&
    typeof data.idTokenClaims === "object" &&
    !Array.isArray(data.idTokenClaims);

  const hasApiKey = typeof data.apiKey === "string" && data.apiKey.length > 0;

  return hasToken || hasApiKey;
}

/**
 * Handle incoming AUTH_CONTEXT messages from the parent.
 */
function handleMessage(event, allowedOrigins) {
  if (!isOriginAllowed(event.origin, allowedOrigins)) {
    if (event.data?.type === AUTH_CONTEXT) {
      logger.warn(
        "[EmbedAuth] AUTH_CONTEXT rejected — origin not in allowlist.",
        "\n  received:", event.origin,
        "\n  allowed: ", allowedOrigins,
      );
    }
    return;
  }

  const { data } = event;
  if (!data || data.type !== AUTH_CONTEXT) {
    return;
  }

  parentOrigin = event.origin;

  if (!isValidAuthPayload(data)) {
    logger.error("[EmbedAuth] Received AUTH_CONTEXT with invalid payload, ignoring");
    return;
  }

  logger.debug("[EmbedAuth] Received AUTH_CONTEXT from parent, origin:", event.origin);

  cachedToken = {
    accessToken: data.accessToken,
    idToken: data.idToken,
    idTokenClaims: data.idTokenClaims,
    apiKey: data.apiKey || null,
  };

  if (typeof data.tenant === "string" && data.tenant.length > 0) {
    setCurrentTenant(data.tenant);
  }

  // Resolve initial token wait
  if (pendingTokenResolve) {
    clearTimeout(pendingTokenTimeout);
    pendingTokenResolve(cachedToken);
    pendingTokenResolve = null;
    pendingTokenPromise = null;
    pendingTokenTimeout = null;
  }

  // Resolve pending refresh wait
  if (pendingRefreshResolve) {
    clearTimeout(pendingRefreshTimeout);
    pendingRefreshResolve(cachedToken);
    pendingRefreshResolve = null;
    pendingRefreshPromise = null;
    pendingRefreshTimeout = null;
  }
}

/**
 * Initialize the postMessage listener for auth messages.
 * Call once during embed-mode initialization.
 */
export function initEmbedAuthListener() {
  const allowedOrigins = getAllowedOrigins();

  if (allowedOrigins.length === 0) {
    logger.error(
      "[EmbedAuth] REACT_APP_ALLOWED_PARENT_ORIGIN is not configured. Auth bridge will not work."
    );
    return;
  }

  const listener = (event) => handleMessage(event, allowedOrigins);
  window.addEventListener("message", listener);
  cleanupListener = () => window.removeEventListener("message", listener);

  logger.debug("[EmbedAuth] Listener initialized. Allowed origins:", allowedOrigins);
}

/**
 * Send AUTH_READY to the parent so it knows the iframe is listening.
 */
export function sendReady() {
  const allowedOrigins = getAllowedOrigins();

  if (window.parent === window) {
    logger.warn("[EmbedAuth] Not inside an iframe, skipping AUTH_READY");
    return;
  }

  // Send to each allowed origin (only the matching one will be received)
  allowedOrigins.forEach((origin) => {
    window.parent.postMessage({ type: AUTH_READY }, origin);
  });

  logger.debug("[EmbedAuth] AUTH_READY sent to parent");
}

/**
 * Get the current token from the parent.
 * If no token has been received yet, waits for the first AUTH_CONTEXT (with timeout).
 * All concurrent callers share the same promise.
 * @returns {Promise<{accessToken: string, idToken: string, idTokenClaims: object}>}
 */
export function getEmbedToken() {
  if (cachedToken) {
    return Promise.resolve(cachedToken);
  }

  // Return the existing pending promise so all callers wait on the same one
  if (pendingTokenPromise) {
    return pendingTokenPromise;
  }

  const TIMEOUT_MS = 15000;

  pendingTokenPromise = new Promise((resolve, reject) => {
    pendingTokenResolve = resolve;

    pendingTokenTimeout = setTimeout(() => {
      if (pendingTokenResolve) {
        reject(
          new Error("[EmbedAuth] Timeout: parent did not send AUTH_CONTEXT within 15s")
        );
        pendingTokenResolve = null;
        pendingTokenPromise = null;
        pendingTokenTimeout = null;
      }
    }, TIMEOUT_MS);
  });

  return pendingTokenPromise;
}

/**
 * Ask the parent to refresh the token (e.g. after a 401).
 * Returns a promise that resolves when the parent responds with a new AUTH_CONTEXT.
 * Concurrent callers share the same promise (deduplicates requests to parent).
 * @returns {Promise<{accessToken: string, idToken: string, idTokenClaims: object}>}
 */
export function requestTokenRefresh() {
  if (!parentOrigin) {
    logger.warn("[EmbedAuth] Cannot request refresh: parent origin unknown");
    return Promise.reject(new Error("[EmbedAuth] Parent origin unknown"));
  }

  // Return the existing pending promise so concurrent 401s share the same refresh
  if (pendingRefreshPromise) {
    return pendingRefreshPromise;
  }

  window.parent.postMessage({ type: REFRESH_TOKEN }, parentOrigin);
  logger.debug("[EmbedAuth] REFRESH_TOKEN sent to parent");

  const REFRESH_TIMEOUT_MS = 10000;

  pendingRefreshPromise = new Promise((resolve, reject) => {
    pendingRefreshResolve = resolve;

    pendingRefreshTimeout = setTimeout(() => {
      if (pendingRefreshResolve) {
        cachedToken = null;
        reject(new Error("[EmbedAuth] Timeout: parent did not respond to REFRESH_TOKEN within 10s"));
        pendingRefreshResolve = null;
        pendingRefreshPromise = null;
        pendingRefreshTimeout = null;
      }
    }, REFRESH_TIMEOUT_MS);
  });

  return pendingRefreshPromise;
}

/**
 * Get the API key received from the parent.
 * The parent sends it in AUTH_CONTEXT so the iframe doesn't need to extract from claims.
 *
 * WORKAROUND: see iframe-auth.ts getStoredApiKey for why this is needed.
 * Unnecessary if B2C policy includes api_keys in all token flows,
 * or if backend resolves API key from Bearer token server-side.
 *
 * @returns {string|null}
 */
export function getEmbedApiKey() {
  return cachedToken?.apiKey ?? null;
}

/**
 * Notify the parent window that a new chat has been created.
 * Only sends when running inside an iframe with a known parent origin.
 *
 * @param {string|number} chatId - The newly created chat ID.
 * @param {string} [currentResponse=""] - Last assistant message content, for report pre-fill.
 */
export function sendChatCreated(chatId, currentResponse = "") {
  if (!parentOrigin) {
    logger.warn("[EmbedAuth] Cannot send CHAT_CREATED: parent origin unknown");
    return;
  }

  if (window.parent === window) {
    logger.warn("[EmbedAuth] Not inside an iframe, skipping CHAT_CREATED");
    return;
  }

  window.parent.postMessage(
    { type: "CHAT_CREATED", chatId, currentResponse },
    parentOrigin,
  );

  logger.debug("[EmbedAuth] CHAT_CREATED sent to parent, chatId:", chatId);
}

/**
 * Notify the parent window that an assistant reply is ready (or has been
 * seen), so a cross-origin host can badge/clear its own browser tab.
 *
 * @param {boolean} unread - true once a reply arrived while unfocused, false once seen again.
 */
export function sendUnreadReplyStatus(unread) {
  if (!parentOrigin) {
    logger.warn("[EmbedAuth] Cannot send UNREAD_REPLY_STATUS: parent origin unknown");
    return;
  }

  if (window.parent === window) {
    logger.warn("[EmbedAuth] Not inside an iframe, skipping UNREAD_REPLY_STATUS");
    return;
  }

  window.parent.postMessage({ type: "UNREAD_REPLY_STATUS", unread }, parentOrigin);

  logger.debug("[EmbedAuth] UNREAD_REPLY_STATUS sent to parent, unread:", unread);
}

/**
 * Notify the parent window that an async chat request is running, so a
 * cross-origin host can badge/clear its own browser tab. Unlike
 * UNREAD_REPLY_STATUS, this is sent regardless of tab focus and stays on for
 * the whole duration of the request.
 *
 * @param {boolean} running - true while an async request is in flight.
 */
export function sendRunningStatus(running) {
  if (!parentOrigin) {
    logger.warn("[EmbedAuth] Cannot send RUNNING_STATUS: parent origin unknown");
    return;
  }

  if (window.parent === window) {
    logger.warn("[EmbedAuth] Not inside an iframe, skipping RUNNING_STATUS");
    return;
  }

  window.parent.postMessage({ type: "RUNNING_STATUS", running }, parentOrigin);

  logger.debug("[EmbedAuth] RUNNING_STATUS sent to parent, running:", running);
}

/**
 * Clean up the message listener and reset all state.
 */
export function destroyEmbedAuth() {
  if (cleanupListener) {
    cleanupListener();
    cleanupListener = null;
  }
  clearTimeout(pendingTokenTimeout);
  clearTimeout(pendingRefreshTimeout);
  cachedToken = null;
  parentOrigin = null;
  allowedOriginsCache = null;
  pendingTokenPromise = null;
  pendingTokenResolve = null;
  pendingTokenTimeout = null;
  pendingRefreshPromise = null;
  pendingRefreshResolve = null;
  pendingRefreshTimeout = null;
}
